import { auth } from '../firebase';
import {
  DeviceScheduleSettings,
  DeviceScheduleSettingsUpdate,
  DEFAULT_SCHEDULE_SETTINGS,
} from '../types/schedule';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

class ScheduleSettingsService {
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const token = await user.getIdToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response.json();
  }

  /**
   * Get schedule settings for a device
   */
  async getScheduleSettings(deviceId: string): Promise<DeviceScheduleSettings> {
    try {
      return await this.request<DeviceScheduleSettings>(
        `/devices/${deviceId}/schedule-settings/`
      );
    } catch {
      // Return defaults if settings don't exist yet
      return DEFAULT_SCHEDULE_SETTINGS;
    }
  }

  /**
   * Update schedule settings for a device
   */
  async updateScheduleSettings(
    deviceId: string,
    updates: DeviceScheduleSettingsUpdate
  ): Promise<DeviceScheduleSettings> {
    return this.request<DeviceScheduleSettings>(
      `/devices/${deviceId}/schedule-settings/`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );
  }
}

export const scheduleSettingsService = new ScheduleSettingsService();
